import { Link } from "react-router-dom";
import { ArrowRight, FileText, Package, Ship, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import { SEO_META, IMAGES, PACKING_GALLERY } from "../config/site";

const DOCS = [
  { icon: FileText, title: "Commercial Quotation / Proforma Invoice", text: "A structured quotation based on your product specifications, quantity and customization, followed by a proforma invoice to confirm the order." },
  { icon: ClipboardList, title: "Product Specifications", text: "Confirmed style, fabric, colors, sizes and branding details documented before production begins." },
  { icon: Package, title: "Packing Details", text: "Packing method, carton breakdown and packing list prepared for your shipment." },
  { icon: FileText, title: "Commercial Invoice & Packing List", text: "Standard commercial export documentation issued with the dispatch of your order." },
  { icon: Ship, title: "Shipping Coordination", text: "Coordination with your nominated forwarder or shipping partner, with export documentation as applicable." },
  { icon: Ship, title: "FOB Shipment Options", text: "FOB shipment terms available where mutually agreed as part of your order confirmation." },
];

export default function ExportPage() {
  const meta = SEO_META.export;
  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="relative overflow-hidden border-b border-obsidian/10" data-testid="export-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.industrial} alt="Export-ready industrial workwear production" className="h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-obsidian/75" />
        </div>
        <div className="container-site relative py-24 lg:py-32">
          <Reveal>
            <p className="eyebrow !text-white/60">Export & Documentation</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[1.06] tracking-tight text-white sm:text-5xl">
              Built in India. Ready for global business.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">
              We work with importers, distributors and institutional buyers — from the first enquiry through
              quotation, production and documented export dispatch.
            </p>
            <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="export-hero-quote-btn" className="btn-primary mt-8">
              Discuss Your Import Requirement <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-16 lg:py-24" data-testid="export-process">
        <Reveal>
          <p className="eyebrow">How an export enquiry works</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
            A structured path from enquiry to shipment
          </h2>
        </Reveal>
        <ol className="mt-12 grid gap-px border border-obsidian/10 bg-obsidian/10 md:grid-cols-2 lg:grid-cols-4">
          {[
            ["Enquiry", "Send your requirement with product type, quantity and destination."],
            ["Quotation", "We review specifications and respond with a commercial quotation."],
            ["Confirmation", "Specifications, branding and delivery terms are confirmed in a proforma invoice."],
            ["Production & Dispatch", "Your order is produced, packed and dispatched with applicable export documentation."],
          ].map(([t, d], i) => (
            <Reveal as="li" key={t} delay={i * 0.08} className="bg-ivory">
              <div className="h-full p-7">
                <span className="font-mono text-xs font-semibold tracking-[0.2em] text-cleef">STEP {i + 1}</span>
                <h3 className="mt-3 font-display text-lg font-bold tracking-tight">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{d}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="border-t border-obsidian/10 bg-white" data-testid="export-docs">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <p className="eyebrow">Documentation & Terms</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
              Commercial clarity at every stage
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2 lg:grid-cols-3">
            {DOCS.map((d, i) => (
              <Reveal key={d.title} delay={(i % 3) * 0.08} className="bg-white">
                <div className="h-full p-7">
                  <d.icon className="h-5 w-5 text-cleef" />
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-8 max-w-2xl text-xs leading-relaxed text-obsidian/45">
              Shipment terms, documentation and delivery arrangements are confirmed per order during the
              quotation process. Payment and banking details are shared privately with confirmed buyers only.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="container-site py-16 lg:py-24" data-testid="packing-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Packing & Dispatch</p>
              <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
                Packed for the journey, documented for customs
              </h2>
              <p className="mt-4 max-w-xl text-base text-obsidian/60">
                Folding, individual packing, carton marking and labeling options are confirmed per order —
                and every dispatch is paired with its packing list and commercial documentation.
              </p>
            </div>
            <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="packing-quote-btn" className="btn-outline">
              Plan Your Shipment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PACKING_GALLERY.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <figure className="group border border-obsidian/10 bg-white" data-testid={`packing-card-${i}`}>
                <div className="overflow-hidden">
                  <img src={p.img} alt={p.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]" />
                </div>
                <figcaption className="p-5">
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-cleef">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-1.5 font-display text-base font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-obsidian/55">{p.text}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABlock title="Discuss your import requirement." sub="Share your product specifications, quantities and destination — our team will respond with a structured quotation." />
    </>
  );
}
